/**
 * Rhythmz — Preset catalog
 * ────────────────────────
 * Loads the Milkdrop preset library via Vite glob and organizes it into a
 * "Featured" set (curated by renowned author names) + the full library.
 * Presets are loaded lazily on demand to keep initial load fast.
 */

// Glob all presets; eager: false means each is a dynamic import callable on demand.
const presetGlob = import.meta.glob('./milkdrop/*.json', { import: 'default' });

export interface PresetEntry {
  id: string;       // filename without extension
  name: string;     // display name (cleaned up)
  load: () => Promise<unknown>;
}

// Renowned Milkdrop authors whose presets are reliably high quality.
const FEATURED_AUTHORS = [
  'geiss', 'flexi', 'martin', 'rovastar', 'aderrasi', 'zylot',
  'adamfx', 'stahlregen', 'eos', 'bmelgren', 'shifter', 'krash',
];

function cleanName(filename: string): string {
  return filename
    .replace(/\.json$/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

const allEntries: PresetEntry[] = Object.entries(presetGlob).map(([path, loader]) => {
  const filename = path.split('/').pop() ?? path;
  const id = filename.replace(/\.json$/, '');
  return { id, name: cleanName(filename), load: loader as () => Promise<unknown> };
});

// Featured: presets by renowned authors (case-insensitive match in filename).
const featuredEntries = allEntries
  .filter((e) => FEATURED_AUTHORS.some((a) => e.id.toLowerCase().includes(a)))
  .slice(0, 60);

export const FEATURED_PRESETS = featuredEntries.length > 20 ? featuredEntries : allEntries.slice(0, 60);
export const ALL_PRESETS = allEntries;
export const PRESET_COUNT = allEntries.length;

/** Load N presets eagerly (for the initial featured set the engine boots with). */
export async function loadPresetBatch(entries: PresetEntry[]): Promise<{ name: string; data: unknown }[]> {
  const loaded = await Promise.all(entries.slice(0, 60).map(async (e) => ({ name: e.name, data: await e.load() })));
  return loaded;
}
